import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getGeneralInfo } from 'reducers/users';
import BaseForm from 'containers/Base/BaseForm';
import { isBase64 } from 'utils/checkImage';
import {
  validationSchema,
  initialState,
  initialValues,
  generalSection,
  detailedSection,
} from './options';
import staticText from 'static/text_display.json';
import { translatedKeyValueText } from 'utils/methods/helpers';
import { BusinessClientInfo } from 'utils/constants/informationText';
import { HelpLinks } from 'utils/constants/helpLinks';
import { getItemOrDefaultToValue } from 'utils/details';
//actions
import { actions as businessClientActions } from '../Modules/dataGridTable';
import { closeDetails, showSnackbar } from 'reducers/ui';
import { getGlobalDepartments } from 'utils/personnelHelper';
import { getGlobalDepartmentRecords } from 'selectors/globalSelectors';
import {
  getBusinessClientDetailsTransformed,
  customerEmbeddedDetails,
  getBusinessClientDetailsRaw,
} from '../Selectors/businessClientSelectors';
import isEmpty from 'lodash/isEmpty';
import { actions as embeddedFormActions } from 'modules/embeddedForms/reducer';
import { PAGE_URLS } from 'utils/constants';
import { merge } from 'lodash';
import { getSecurityCompanyDetail } from 'reducers/securityCompany';

const BusinessClientForm = ({
  createBusinessClient,
  updateBusinessClient,
  businessClient,
  editMode,
  closeForm,
  generalInfo,
  countries,
  languages,
  userAssignedDepartments,
  // from embedded form component
  embeddedForm,
  disablePresist,
  persistentStateName,
  closeDetail,
}) => {
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState();
  const [businessClientEmbedded, setBusinessClient] = useState();
  const [editBusinessClient, setEditBusinessClient] = useState(false);

  const embeddedCustomerDetails = useSelector(
    state => customerEmbeddedDetails(state),
    shallowEqual,
  );
  const securityCompanyDetails = useSelector(
    state => getSecurityCompanyDetail(state),
    shallowEqual,
  );

  let bizClient;
  if (!isEmpty(businessClient)) {
    // if in edit mode businesClient will not be empty
    bizClient = businessClient;
  } else {
    // when a business client is created before creating the embedded customer in non edit view
    bizClient = businessClientEmbedded;
  }

  useEffect(() => {
    // close the detail panel when it is open and create form is clicked
    if (!editMode && !isEmpty(bizClient)) {
      dispatch(closeDetails(PAGE_URLS.BUSINESS_CLIENTS.link));
      if (typeof closeDetail === 'function') closeDetail();
    }
  }, [editMode]);

  const onCreate = values => {
    if (editBusinessClient) {
      onEdit(values);
      return;
    }
    const ids = values?.departments &&
      values?.departments.length > 0 && {
        departmentIds: getItemOrDefaultToValue(
          values,
          'departments',
          undefined,
        )?.map(m => m.id),
      };
    let data;
    const businessClientData = {
      businessClientId:
        values.businessClientId === '' ? null : values.businessClientId,
      name: values.companyName,
      imageBase64: values.image,
      address: values.address,
      securityCompanyId: generalInfo.securityCompanyId,
      securityCompanyName: generalInfo.securityCompanyName,
      archived: values.archived,
      description: values.description,
      ...(ids && { departmentIds: ids.departmentIds }),
    };
    if (!isEmpty(values.contact)) {
      // const contactData = getContactData(values);
      const contactData = values.contact.value;
      data = {
        ...businessClientData,
        contact: contactData,
      };
    } else {
      data = { ...businessClientData };
    }

    createBusinessClient({
      businessClient: data,
      ...(embeddedForm && { embeddedForm }),
    });
    if (embeddedForm) {
      closeForm();
    }
  };

  const onEdit = values => {
    const ids = values?.departments &&
      values?.departments.length > 0 && {
        departmentIds: getItemOrDefaultToValue(
          values,
          'departments',
          undefined,
        )?.map(m => m.id),
      };

    let data;
    const businessClientData = {
      businessClientId:
        values.businessClientId === '' ? null : values.businessClientId,
      name: values.companyName,
      address: values.address,
      securityCompanyId: generalInfo.securityCompanyId,
      securityCompanyName: generalInfo.securityCompanyName,
      archived: values.archived,
      description: values.description,
      ...(ids && { departmentIds: ids.departmentIds }),
    };
    if (isBase64(values.image)) {
      businessClientData['imageBase64'] = values.image;
    }
    if (!isEmpty(values.contact)) {
      data = {
        ...businessClientData,
        contact: values.contact.value,
      };
    } else {
      data = { ...businessClientData };
    }

    updateBusinessClient({
      id: bizClient.others.id.value,
      businessClient: data,
    });
    // close the form after update
    if (embeddedForm) {
      closeForm();
    }
  };

  const monitorFormChanges = vals => {
    setFormValues(vals);
    if (!isEmpty(embeddedCustomerDetails)) {
      merge(vals, { contact: embeddedCustomerDetails });
    }
    if (!isEmpty(vals.contact) && !isEmpty(bizClient?.others)) {
      // if both customer and business client exists. Here ID is BizClient id
      setEditBusinessClient(true);
    } else {
      setEditBusinessClient(false);
    }
  };
  const transformedInitialValues = editMode
    ? initialValues(bizClient)
    : initialState;

  useEffect(() => {
    if (!isEmpty(embeddedCustomerDetails)) {
      // clear embedded details
      dispatch(embeddedFormActions.clearEmbeddedFormDetails());
    }
    return () => {
      // clear embedded details
      dispatch(embeddedFormActions.clearEmbeddedFormDetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedCustomerDetails]);

  return (
    <>
      <BaseForm
        imageType="Establishment"
        edit={editMode}
        initialValues={{ ...transformedInitialValues, ...formValues }}
        formTitle={
          editMode
            ? staticText.managerial_view.business_client.edit_default
            : staticText.managerial_view.business_client.create
        }
        saveHoverText={
          editMode
            ? translatedKeyValueText('update_value_title', {
                value: bizClient.generalInfo.companyName.value,
              })
            : staticText.managerial_view.business_client.create
        }
        closeForm={closeForm}
        onSubmit={editMode ? onEdit : onCreate}
        validationSchema={validationSchema()}
        generalSection={generalSection(userAssignedDepartments)}
        detailedSection={detailedSection({
          enableApiCall: isEmpty(bizClient),
          embeddedBizID: bizClient && bizClient.others.id.value,

          ...(bizClient && {
            embeddedselectedBusinessClient: {
              id: bizClient.others.id.value,
              businessClientId: bizClient.generalInfo.businessClientId.value,
              name: bizClient.generalInfo.companyName.value,
              contact: bizClient.others.contact.value,
              departmentIds: bizClient.generalInfo.departments.value,
              archived: false,
              address: bizClient.detailedInfo.address.value,
              description: bizClient.detailedInfo.description.value,
              securityCompanyId: securityCompanyDetails.id,
              securityCompanyName: securityCompanyDetails.name,
              imageUrl: bizClient.others.image.value,
            },
          }),
          editMode,
          countries,
          languages,
          companyName: formValues?.companyName,
          businessClientPayload: {
            archived: false,
            name: formValues?.companyName,
            securityCompanyId: generalInfo.securityCompanyId,
            securityCompanyName: generalInfo.securityCompanyName,
          },
          embeddedBizClient: setBusinessClient,
        })}
        onChange={monitorFormChanges}
        hoverText={editMode ? bizClient.generalInfo.companyName.value : ''}
        isImageMandatory={false}
        imgaeLabel={'Company Logo'}
        note={BusinessClientInfo.note}
        formInfo={{
          link: HelpLinks.businessClientLink,
          value: HelpLinks.businessClientText,
        }}
        disablePresist={disablePresist}
        persistentStateName={persistentStateName}
      />
    </>
  );
};

BusinessClientForm.propTypes = {
  createBusinessClient: PropTypes.func,
  updateBusinessClient: PropTypes.func,
  closeDetail: PropTypes.func,
  businessClient: PropTypes.object,
  businessClientRaw: PropTypes.object,
  editMode: PropTypes.bool,
  embeddedForm: PropTypes.bool,
  disablePresist: PropTypes.bool,
  closeForm: PropTypes.func.isRequired,
  generalInfo: PropTypes.object.isRequired,
  countries: PropTypes.array,
  languages: PropTypes.array,
  userAssignedDepartments: PropTypes.array,
  persistentStateName: PropTypes.string,
};

const mapStateToProps = state => ({
  businessClient: getBusinessClientDetailsTransformed(state),
  businessClientRaw: getBusinessClientDetailsRaw(state),
  generalInfo: getGeneralInfo(state),
  countries: state.utils.countries,
  globalDepartmentList: getGlobalDepartments(getGlobalDepartmentRecords(state)),
  languages: state.utils.languages,
});

const mapDispatchToProps = dispatch => ({
  createBusinessClient: ({ businessClient, embeddedForm }) =>
    dispatch(
      businessClientActions.createRowEntry({ businessClient, embeddedForm }),
    ),
  updateBusinessClient: ({ id, businessClient }) =>
    dispatch(businessClientActions.updateRowEntry({ id, businessClient })),
  showSnackBar: ({ typeOfSnackbar, message }) =>
    dispatch(showSnackbar({ typeOfSnackbar, message })),
});

export default connect(mapStateToProps, mapDispatchToProps)(BusinessClientForm);
